/**
 * PowerPoint slide templates for lesson export
 */

// @ts-ignore - pptxgenjs types may not be available until package is installed
import pptxgen from 'pptxgenjs';
import { Lesson, TimelineStep } from '../types';
import { htmlToText } from '../utils/htmlToPowerPoint';
import { createResourceVisualization, getResourceTypeName } from '../utils/resourceVisualizer';
import { sanitizeTextForPowerPoint, sanitizeColor } from '../utils/powerPointUtils';

/**
 * Helper function to create safe text options for PowerPoint
 * Uses only the most basic and safe properties
 */
function createSafeTextOptions(options: {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  fontSize?: number;
  color?: string;
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  italic?: boolean;
} = {}): any {
  const safeOptions: any = {};
  
  // Position and size - ensure valid values
  if (options.x !== undefined && !isNaN(options.x)) safeOptions.x = Math.max(0, Math.min(10, options.x));
  if (options.y !== undefined && !isNaN(options.y)) safeOptions.y = Math.max(0, Math.min(7.5, options.y));
  if (options.w !== undefined && !isNaN(options.w)) safeOptions.w = Math.max(0.1, Math.min(10, options.w));
  if (options.h !== undefined && !isNaN(options.h)) safeOptions.h = Math.max(0.1, Math.min(7.5, options.h));
  
  // Font size - ensure valid range
  if (options.fontSize !== undefined && !isNaN(options.fontSize)) {
    const size = Math.max(8, Math.min(72, Math.round(options.fontSize)));
    if (size > 0) safeOptions.fontSize = size;
  }
  
  // Color - must be valid hex
  if (options.color) {
    const color = sanitizeColor(options.color);
    if (color && color.length === 6) {
      safeOptions.color = color;
    }
  }
  
  // Alignment
  if (options.align) {
    safeOptions.align = options.align;
  }
  
  // Text formatting
  if (options.bold !== undefined) {
    safeOptions.bold = Boolean(options.bold);
  }
  if (options.italic !== undefined) {
    safeOptions.italic = Boolean(options.italic);
  }
  
  return safeOptions;
}

export interface ExportOptions {
  includeResources?: boolean;
  includeAssessments?: boolean;
  includeNotes?: boolean;
  detailLevel?: 'summary' | 'complete';
  theme?: 'light' | 'dark';
  customColors?: {
    warmup?: string;
    launch?: string;
    direct_instruction?: string;
    guided_practice?: string;
    independent_practice?: string;
    closing?: string;
  };
}

// Default colors for step types
const DEFAULT_STEP_COLORS = {
  warmup: '4472C4',
  launch: '70AD47',
  direct_instruction: 'FFC000',
  guided_practice: '5B9BD5',
  independent_practice: '7030A0',
  closing: 'C00000'
};

/**
 * Gets color for a step type
 * Note: Currently unused as background colors can cause PowerPoint repair issues
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getStepColor(stepType: string, options: ExportOptions): string {
  if (options.customColors?.[stepType as keyof typeof options.customColors]) {
    return options.customColors[stepType as keyof typeof options.customColors]!;
  }
  return DEFAULT_STEP_COLORS[stepType as keyof typeof DEFAULT_STEP_COLORS] || '4472C4';
}

/**
 * Creates the title slide
 */
export function createTitleSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  const slide = pptx.addSlide();
  
  // Title - sanitize and ensure non-empty
  const title = sanitizeTextForPowerPoint(lesson.metadata.title) || 'Untitled Lesson';
  if (title && title.length > 0) {
    slide.addText(title, createSafeTextOptions({
      x: 0.5,
      y: 1.5,
      w: 9,
      h: 1,
      fontSize: 36,
      color: '2F5597'
    }));
  }
  
  // Subtitle
  const subtitle = sanitizeTextForPowerPoint(`${lesson.metadata.subject || ''} - Grade ${lesson.metadata.grade || ''}`);
  if (subtitle && subtitle.length > 0) {
    slide.addText(subtitle, createSafeTextOptions({
      x: 0.5,
      y: 2.8,
      w: 9,
      h: 0.6,
      fontSize: 20,
      color: '5B9BD5'
    }));
  }
  
  // Metadata
  try {
    const dateStr = lesson.metadata.created_at 
      ? new Date(lesson.metadata.created_at).toLocaleDateString() 
      : new Date().toLocaleDateString();
    
    const metadataText = sanitizeTextForPowerPoint([
      `Durée: ${lesson.metadata.duration_minutes || 0} minutes`,
      `Langue: ${(lesson.metadata.language || 'en').toUpperCase()}`,
      `Version: ${lesson.metadata.version || '1.0.0'}`,
      `Date: ${dateStr}`
    ].join('\n'));
    
    if (metadataText && metadataText.length > 0) {
      slide.addText(metadataText, createSafeTextOptions({
        x: 0.5,
        y: 4.5,
        w: 9,
        h: 1.5,
        fontSize: 14,
        color: '666666'
      }));
    }
  } catch (error) {
    console.warn('Error creating metadata text:', error);
  }
}

/**
 * Creates the objectives slide
 */
export function createObjectivesSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  const slide = pptx.addSlide();
  
  slide.addText('Objectifs d\'Apprentissage', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '2F5597'
  }));
  
  if (lesson.objectives && lesson.objectives.length > 0) {
    const objectives = lesson.objectives
      .filter(obj => obj && obj.trim())
      .map((obj, index) => `• ${index + 1}. ${sanitizeTextForPowerPoint(obj)}`)
      .join('\n');
    
    if (objectives) {
      slide.addText(objectives, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 18,
        color: '333333'
      }));
    }
  }
}

/**
 * Creates the success criteria slide
 */
export function createSuccessCriteriaSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  const slide = pptx.addSlide();
  
  slide.addText('Critères de Réussite', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '70AD47'
  }));
  
  if (lesson.success_criteria && lesson.success_criteria.length > 0) {
    const criteria = lesson.success_criteria
      .filter(c => c && c.trim())
      .map((criterion) => `• ${sanitizeTextForPowerPoint(criterion)}`)
      .join('\n');
    
    if (criteria && criteria.length > 0) {
      slide.addText(criteria, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 16,
        color: '333333'
      }));
    }
  }
}

/**
 * Creates the standards and prerequisites slide
 */
export function createStandardsSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  const slide = pptx.addSlide();
  
  slide.addText('Alignement avec les Standards', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '2F5597'
  }));
  
  // Standards
  if (lesson.standards_alignment && lesson.standards_alignment.length > 0) {
    slide.addText('Standards:', createSafeTextOptions({
      x: 0.8,
      y: 1.5,
      w: 4,
      h: 0.5,
      fontSize: 18,
      color: '333333'
    }));
    
    const standards = lesson.standards_alignment
      .filter(s => s && s.trim())
      .map(s => `• ${sanitizeTextForPowerPoint(s)}`)
      .join('\n');
    
    if (standards) {
      slide.addText(standards, createSafeTextOptions({
        x: 0.8,
        y: 2.2,
        w: 4,
        h: 3,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
  
  // Prerequisites
  if (lesson.prerequisites) {
    slide.addText('Prérequis:', createSafeTextOptions({
      x: 5.2,
      y: 1.5,
      w: 4,
      h: 0.5,
      fontSize: 18,
      color: '333333'
    }));
    
    const prerequisites = sanitizeTextForPowerPoint(lesson.prerequisites);
    if (prerequisites && prerequisites.length > 0) {
      slide.addText(prerequisites, createSafeTextOptions({
        x: 5.2,
        y: 2.2,
        w: 4,
        h: 3,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
}

/**
 * Creates the materials slide
 */
export function createMaterialsSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  const slide = pptx.addSlide();
  
  slide.addText('Matériel Nécessaire', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '2F5597'
  }));
  
  // Digital materials
  if (lesson.materials.digital && lesson.materials.digital.length > 0) {
    slide.addText('Matériel Numérique:', createSafeTextOptions({
      x: 0.8,
      y: 1.5,
      w: 4,
      h: 0.5,
      fontSize: 18,
      color: '333333'
    }));
    
    const digital = lesson.materials.digital
      .filter(m => m && m.trim())
      .map(m => `• ${sanitizeTextForPowerPoint(m)}`)
      .join('\n');
    
    if (digital) {
      slide.addText(digital, createSafeTextOptions({
        x: 0.8,
        y: 2.2,
        w: 4,
        h: 3,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
  
  // Physical alternatives
  if (lesson.materials.physical_alternatives && lesson.materials.physical_alternatives.length > 0) {
    slide.addText('Alternatives Physiques:', createSafeTextOptions({
      x: 5.2,
      y: 1.5,
      w: 4,
      h: 0.5,
      fontSize: 18,
      color: '333333'
    }));
    
    const physical = lesson.materials.physical_alternatives
      .filter(m => m && m.trim())
      .map(m => `• ${sanitizeTextForPowerPoint(m)}`)
      .join('\n');
    
    if (physical) {
      slide.addText(physical, createSafeTextOptions({
        x: 5.2,
        y: 2.2,
        w: 4,
        h: 3,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
}

/**
 * Creates slides for a timeline step
 */
export async function createTimelineStepSlides(
  step: TimelineStep,
  stepIndex: number,
  lesson: Lesson,
  pptx: pptxgen,
  options: ExportOptions
): Promise<void> {
  // Note: stepColor removed as background colors can cause PowerPoint repair issues
  const stepTypeNames: Record<string, string> = {
    warmup: 'Échauffement',
    launch: 'Lancement',
    direct_instruction: 'Instruction Directe',
    guided_practice: 'Pratique Guidée',
    independent_practice: 'Pratique Indépendante',
    closing: 'Clôture'
  };
  
  // Slide 1: Step Header
  const headerSlide = pptx.addSlide();
  // Note: background color removed as it can cause PowerPoint repair issues
  // Instead, we'll use a colored shape or text background if needed
  
  const stepName = sanitizeTextForPowerPoint(step.name) || 'Untitled Step';
  if (stepName && stepName.length > 0) {
    headerSlide.addText(stepName, createSafeTextOptions({
      x: 0.5,
      y: 2,
      w: 9,
      h: 1,
      fontSize: 32,
      color: '000000'
    }));
  }
  
  const stepInfo = sanitizeTextForPowerPoint([
    `Type: ${stepTypeNames[step.step_type] || step.step_type}`,
    `Durée: ${step.minutes || 0} minutes`
  ].join('\n'));
  
  if (stepInfo && stepInfo.length > 0) {
    headerSlide.addText(stepInfo, createSafeTextOptions({
      x: 0.5,
      y: 3.5,
      w: 9,
      h: 1,
      fontSize: 18,
      color: '000000'
    }));
  }
  
  // Slide 2: Teacher Script
  if (step.teacher_script && step.teacher_script.length > 0 && options.detailLevel !== 'summary') {
    const scriptSlide = pptx.addSlide();
    
    scriptSlide.addText('Script de l\'Enseignant', createSafeTextOptions({
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 0.8,
      fontSize: 24,
      color: '2F5597'
    }));
    
    const scriptText = step.teacher_script
      .filter(script => script && script.trim())
      .map((script, index) => {
        const cleaned = sanitizeTextForPowerPoint(htmlToText(script));
        return cleaned ? `${index + 1}. ${cleaned}` : '';
      })
      .filter(text => text)
      .join('\n\n');
    
    if (scriptText && scriptText.length > 0) {
      scriptSlide.addText(scriptText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
  
  // Slide 3: Student Actions
  if (step.student_actions && step.student_actions.length > 0 && options.detailLevel !== 'summary') {
    const actionsSlide = pptx.addSlide();
    
    actionsSlide.addText('Actions des Élèves', createSafeTextOptions({
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 0.8,
      fontSize: 24,
      color: '70AD47'
    }));
    
    const actionsText = step.student_actions
      .filter(action => action && action.trim())
      .map(action => `• ${sanitizeTextForPowerPoint(action)}`)
      .join('\n');
    
    if (actionsText && actionsText.length > 0) {
      actionsSlide.addText(actionsText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 14,
        color: '333333'
      }));
    }
  }
  
  // Slide 4: Checks for Understanding
  if (step.checks_for_understanding && step.checks_for_understanding.length > 0 && options.detailLevel !== 'summary') {
    const checksSlide = pptx.addSlide();
    
    checksSlide.addText('Vérifications de Compréhension', createSafeTextOptions({
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 0.8,
      fontSize: 24,
      color: 'FFC000'
    }));
    
    const checksText = step.checks_for_understanding
      .map((check, index) => {
        const parts = [
          `${index + 1}. Type: ${check.type || ''}`,
          `   Question: ${sanitizeTextForPowerPoint(check.prompt || '')}`,
          check.look_for ? `   À observer: ${sanitizeTextForPowerPoint(check.look_for)}` : '',
          check.expected ? `   Réponse attendue: ${Array.isArray(check.expected) ? check.expected.map(e => sanitizeTextForPowerPoint(String(e))).join(', ') : sanitizeTextForPowerPoint(String(check.expected))}` : ''
        ].filter(p => p && p.trim()).join('\n');
        return parts;
      })
      .filter(text => text.trim())
      .join('\n\n');
    
    if (checksText && checksText.length > 0) {
      checksSlide.addText(checksText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 12,
        color: '333333'
      }));
    }
  }
  
  // Slide 5: Resources (if included)
  if (options.includeResources && step.resources && step.resources.length > 0) {
    // Process each resource - create separate slides for images and rich text
    for (const stepResource of step.resources) {
      const resource = lesson.resources[stepResource.resourceId];
      if (!resource) continue;
      
      const visualization = createResourceVisualization(resource, stepResource.resourceId);
      const description = sanitizeTextForPowerPoint(visualization.description);
      
      // Handle images - download and insert them
      if (resource.type === 'image' && visualization.imageUrl) {
        try {
          const imageSlide = pptx.addSlide();
          
          // Add resource title/caption if available
          const caption = resource.caption || resource.title || resource.description || resource.alt || resource.alt_text;
          if (caption) {
            imageSlide.addText(sanitizeTextForPowerPoint(caption), createSafeTextOptions({
              x: 0.5,
              y: 0.3,
              w: 9,
              h: 0.6,
              fontSize: 18,
              color: '7030A0',
              bold: true
            }));
          }
          
          // Try to use URL directly first (simpler and more reliable)
          // pptxgenjs can load images from URLs if they're accessible
          const imageUrl = visualization.imageUrl;
          let imageAdded = false;
          let resizedImage: { buffer: ArrayBuffer; width: number; height: number } | null = null;
          
          // Calculate dimensions from the image URL if possible
          // For now, use default dimensions
          const defaultImageWidth = 6; // 6 inches
          const defaultImageHeight = 4.5; // 4.5 inches (maintains 4:3 ratio)
          const xPos = (10 - defaultImageWidth) / 2; // Center horizontally
          const yPos = caption ? 1.2 : 1.5; // Position below caption or at top
          
          // Try using path (URL) first - this is more reliable for external images
          if (imageUrl && !imageUrl.startsWith('data:')) {
            try {
              imageSlide.addImage({
                path: imageUrl,
                x: xPos,
                y: yPos,
                w: defaultImageWidth,
                h: defaultImageHeight
              });
              imageAdded = true;
              console.log(`Image added via URL: ${imageUrl.substring(0, 50)}...`);
            } catch (urlError) {
              console.warn('Failed to add image via URL, trying base64:', urlError);
            }
          }
          
          // Fallback: download and convert to base64
          if (!imageAdded) {
            try {
              const { downloadImageAsBuffer, resizeAndCompressImage } = await import('../utils/imageDownloader');
              const imageBuffer = await downloadImageAsBuffer(imageUrl);
              
              if (imageBuffer) {
                // Resize image for PowerPoint (max 7 inches width, maintain aspect ratio)
                const maxWidthPixels = 672; // 7 inches at 96 DPI
                const maxHeightPixels = 504; // 5.25 inches at 96 DPI
                
                const downloadedResizedImage = await resizeAndCompressImage(
                  imageBuffer,
                  maxWidthPixels,
                  maxHeightPixels
                );
                
                if (downloadedResizedImage) {
                  resizedImage = downloadedResizedImage;
                  // Convert ArrayBuffer to base64 for pptxgenjs
                  const uint8Array = new Uint8Array(downloadedResizedImage.buffer);
                  
                  // Convert to base64 using chunked approach to avoid memory issues
                  let binaryString = '';
                  const chunkSize = 8192; // Process in chunks
                  for (let i = 0; i < uint8Array.length; i += chunkSize) {
                    const chunk = uint8Array.slice(i, i + chunkSize);
                    for (let j = 0; j < chunk.length; j++) {
                      binaryString += String.fromCharCode(chunk[j]);
                    }
                  }
                  const base64String = btoa(binaryString);
                  
                  // Calculate position (center the image)
                  const imageWidth = downloadedResizedImage.width / 96; // Convert pixels to inches
                  const imageHeight = downloadedResizedImage.height / 96;
                  const finalXPos = (10 - imageWidth) / 2; // Center horizontally
                  const finalYPos = caption ? 1.2 : 1.5; // Position below caption or at top
                  
                  // Detect image type from buffer
                  let imageType = 'png'; // Default
                  if (uint8Array.length >= 4) {
                    // PNG signature: 89 50 4E 47
                    if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && 
                        uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
                      imageType = 'png';
                    }
                    // JPEG signature: FF D8
                    else if (uint8Array[0] === 0xFF && uint8Array[1] === 0xD8) {
                      imageType = 'jpeg';
                    }
                    // GIF signature: 47 49 46 38
                    else if (uint8Array[0] === 0x47 && uint8Array[1] === 0x49 && 
                             uint8Array[2] === 0x46 && uint8Array[3] === 0x38) {
                      imageType = 'gif';
                    }
                  }
                  
                  // pptxgenjs expects base64 string with data URI prefix
                  const imageDataUri = `data:image/${imageType};base64,${base64String}`;
                  
                  console.log(`Adding image via base64: type=${imageType}, size=${downloadedResizedImage.width}x${downloadedResizedImage.height}px`);
                  
                  imageSlide.addImage({
                    data: imageDataUri,
                    x: Math.max(0.5, finalXPos),
                    y: finalYPos,
                    w: Math.min(imageWidth, 9),
                    h: Math.min(imageHeight, 5.5)
                  });
                  imageAdded = true;
                }
              }
            } catch (base64Error) {
              console.error('Failed to add image via base64:', base64Error);
            }
          }
          
          if (!imageAdded) {
            throw new Error(`Failed to add image: ${imageUrl}`);
          }
          
          // Add description below image if different from caption
          if (description && description !== caption) {
            // Calculate final Y position based on which method was used
            const finalImageHeight = resizedImage 
              ? Math.min(resizedImage.height / 96, 5.5) 
              : defaultImageHeight;
            const finalYPos = caption ? 1.2 : 1.5;
            
            imageSlide.addText(description, createSafeTextOptions({
              x: 0.8,
              y: finalYPos + finalImageHeight + 0.2,
              w: 8.4,
              h: 1,
              fontSize: 12,
              color: '666666'
            }));
          }
        } catch (error) {
          console.error(`Failed to insert image resource: ${visualization.imageUrl}`, error);
          // Fallback: create a text slide with resource info
          const fallbackSlide = pptx.addSlide();
          fallbackSlide.addText(`${getResourceTypeName(resource.type)}: ${description}`, createSafeTextOptions({
            x: 0.8,
            y: 1.5,
            w: 8.4,
            h: 5,
            fontSize: 14,
            color: '333333'
          }));
        }
      } else if (resource.type === 'rich_text') {
        // Rich text resources - convert HTML to formatted text
        const content = resource.content || resource.html || resource.text || '';
        const title = resource.title || resource.caption || '';
        
        if (content && content.trim()) {
          const richTextSlide = pptx.addSlide();
          
          // Add title if available
          if (title) {
            richTextSlide.addText(sanitizeTextForPowerPoint(title), createSafeTextOptions({
              x: 0.5,
              y: 0.3,
              w: 9,
              h: 0.6,
              fontSize: 20,
              color: '7030A0',
              bold: true
            }));
          }
          
          // Convert HTML to plain text (PowerPoint doesn't support rich formatting easily)
          const plainText = sanitizeTextForPowerPoint(htmlToText(content));
          
          if (plainText && plainText.trim()) {
            // Get font size from resource config if available
            const fontSize = resource.fontSize || resource.font_size;
            const fontSizeNum = fontSize && typeof fontSize === 'string' && fontSize !== 'inherit' 
              ? parseInt(fontSize.replace('px', '')) 
              : 14;
            
            // Get text color from resource config
            let textColor = '333333'; // Default dark gray
            if (resource.textColor || resource.color) {
              const color = sanitizeColor(resource.textColor || resource.color);
              if (color && color.length === 6) {
                textColor = color;
              }
            }
            
            richTextSlide.addText(plainText, createSafeTextOptions({
              x: 0.8,
              y: title ? 1.2 : 1.5,
              w: 8.4,
              h: title ? 5.3 : 5.5,
              fontSize: Math.max(10, Math.min(18, fontSizeNum)),
              color: textColor
            }));
          }
        }
      } else {
        // Other resource types - simple text slide
        const resourceSlide = pptx.addSlide();
        resourceSlide.addText(`${getResourceTypeName(resource.type)}: ${description}`, createSafeTextOptions({
          x: 0.8,
          y: 1.5,
          w: 8.4,
          h: 5,
          fontSize: 14,
          color: '333333'
        }));
      }
    }
  }
}

/**
 * Creates assessment slides
 */
export function createAssessmentSlides(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  if (!options.includeAssessments || !lesson.assessments) {
    return;
  }
  
  const assessmentIds = Object.keys(lesson.assessments);
  if (assessmentIds.length === 0) {
    return;
  }
  
  // Title slide
  const titleSlide = pptx.addSlide();
  titleSlide.addText('Évaluations', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: 'C00000',
    bold: true
  }));
  
  // Process each assessment
  assessmentIds.forEach((assessmentId, index) => {
    const assessment = lesson.assessments[assessmentId];
    const itemCount = assessment.items?.length || 0;
    
    // Assessment header slide
    const assessmentSlide = pptx.addSlide();
    
    // Assessment title
    assessmentSlide.addText(`${index + 1}. ${sanitizeTextForPowerPoint(assessmentId)}`, createSafeTextOptions({
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 0.8,
      fontSize: 24,
      color: '7030A0',
      bold: true
    }));
    
    // Assessment metadata
    const metadataLines: string[] = [];
    if (itemCount > 0) {
      metadataLines.push(`Nombre d'items: ${itemCount}`);
    }
    if (assessment.scoring) {
      metadataLines.push(`Score maximum: ${assessment.scoring.max || 0}`);
      metadataLines.push(`Seuil de maîtrise: ${assessment.scoring.mastery_threshold || 0}`);
    }
    
    if (metadataLines.length > 0) {
      const metadataText = sanitizeTextForPowerPoint(metadataLines.join('\n'));
      assessmentSlide.addText(metadataText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 1.5,
        fontSize: 14,
        color: '333333'
      }));
    }
    
    // Process assessment items
    if (assessment.items && assessment.items.length > 0) {
      assessment.items.forEach((item, itemIndex) => {
        // Item slide
        const itemSlide = pptx.addSlide();
        
        // Item title
        itemSlide.addText(`Item ${itemIndex + 1}`, createSafeTextOptions({
          x: 0.5,
          y: 0.3,
          w: 9,
          h: 0.6,
          fontSize: 20,
          color: '7030A0',
          bold: true
        }));
        
        const itemLines: string[] = [];
        
        // Item prompt/question
        if (item.prompt) {
          itemLines.push(`Question: ${sanitizeTextForPowerPoint(item.prompt)}`);
        }
        
        // Item choices (if multiple choice)
        if (item.choices && item.choices.length > 0) {
          const choicesText = item.choices
            .map((choice, choiceIndex) => {
              const choiceLabel = String.fromCharCode(65 + choiceIndex); // A, B, C, etc.
              const isCorrect = choice === item.answer;
              return `${choiceLabel}. ${sanitizeTextForPowerPoint(String(choice))}${isCorrect ? ' ✓ (Réponse correcte)' : ''}`;
            })
            .join('\n');
          itemLines.push(`\nChoix:\n${choicesText}`);
        } else if (item.answer !== undefined && item.answer !== null) {
          itemLines.push(`\nRéponse correcte: ${sanitizeTextForPowerPoint(String(item.answer))}`);
        }
        
        // Item points
        if (item.points !== undefined) {
          itemLines.push(`\nPoints: ${item.points}`);
        }
        
        // Resource content if referenced
        if (item.resource_id && lesson.resources && lesson.resources[item.resource_id]) {
          const resource = lesson.resources[item.resource_id];
          itemLines.push(`\n\nRessource associée: ${item.resource_id} (Type: ${resource.type})`);
          
          // If it's a quiz resource, include its questions
          if (resource.type === 'quiz' && resource.questions && Array.isArray(resource.questions)) {
            itemLines.push(`\nQuestions de la ressource quiz:`);
            resource.questions.forEach((question: any, qIndex: number) => {
              const qLines: string[] = [];
              qLines.push(`\n  Question ${qIndex + 1}: ${sanitizeTextForPowerPoint(question.prompt || '')}`);
              
              if (question.choices && question.choices.length > 0) {
                const qChoicesText = question.choices
                  .map((choice: any, cIndex: number) => {
                    const choiceLabel = String.fromCharCode(65 + cIndex);
                    const isCorrect = choice === question.answer;
                    return `    ${choiceLabel}. ${sanitizeTextForPowerPoint(String(choice))}${isCorrect ? ' ✓' : ''}`;
                  })
                  .join('\n');
                qLines.push(`  Choix:\n${qChoicesText}`);
              } else if (question.answer !== undefined) {
                qLines.push(`  Réponse: ${sanitizeTextForPowerPoint(String(question.answer))}`);
              }
              
              itemLines.push(qLines.join('\n'));
            });
          }
          
          // If it's a rich_text resource, include its content
          if (resource.type === 'rich_text' && resource.content) {
            const richTextContent = htmlToText(resource.content);
            const sanitizedContent = sanitizeTextForPowerPoint(richTextContent);
            if (sanitizedContent.trim()) {
              itemLines.push(`\n\nContenu de la ressource texte:\n${sanitizedContent}`);
            }
          }
        }
        
        if (itemLines.length > 0) {
          const itemText = sanitizeTextForPowerPoint(itemLines.join('\n'));
          itemSlide.addText(itemText, createSafeTextOptions({
            x: 0.8,
            y: 1.2,
            w: 8.4,
            h: 5.3,
            fontSize: 12,
            color: '333333'
          }));
        }
      });
    }
  });
}

/**
 * Creates misconceptions and differentiation slide
 */
export function createMisconceptionsSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  if (options.detailLevel === 'summary') {
    return;
  }
  
  const slide = pptx.addSlide();
  
  slide.addText('Idées Fausses et Différenciation', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '2F5597'
  }));
  
  let content: string[] = [];
  
  // Misconceptions
  if (lesson.misconceptions_and_responses && lesson.misconceptions_and_responses.length > 0) {
    content.push('Idées Fausses:');
    lesson.misconceptions_and_responses.forEach((mr, index) => {
      const misconception = sanitizeTextForPowerPoint(mr.misconception || '');
      const response = sanitizeTextForPowerPoint(mr.response || '');
      if (misconception) content.push(`${index + 1}. ${misconception}`);
      if (response) content.push(`   Réponse: ${response}`);
      content.push('');
    });
  }
  
  // Differentiation
  if (lesson.differentiation) {
    if (Object.keys(lesson.differentiation.supports || {}).length > 0) {
      content.push('Supports:');
      Object.values(lesson.differentiation.supports).forEach((support, index) => {
        const sanitized = sanitizeTextForPowerPoint(String(support));
        if (sanitized) {
          content.push(`${index + 1}. ${sanitized}`);
        }
      });
      content.push('');
    }
    
    if (Object.keys(lesson.differentiation.extensions || {}).length > 0) {
      content.push('Extensions:');
      Object.values(lesson.differentiation.extensions).forEach((extension, index) => {
        const sanitized = sanitizeTextForPowerPoint(String(extension));
        if (sanitized) {
          content.push(`${index + 1}. ${sanitized}`);
        }
      });
    }
  }
  
  if (content.length > 0) {
    const contentText = sanitizeTextForPowerPoint(content.join('\n'));
    if (contentText && contentText.length > 0) {
      slide.addText(contentText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 12,
        color: '333333'
      }));
    }
  }
}

/**
 * Creates notes slide
 */
export function createNotesSlide(lesson: Lesson, pptx: pptxgen, options: ExportOptions): void {
  if (!options.includeNotes) {
    return;
  }
  
  const slide = pptx.addSlide();
  
  slide.addText('Notes Supplémentaires', createSafeTextOptions({
    x: 0.5,
    y: 0.5,
    w: 9,
    h: 0.8,
    fontSize: 28,
    color: '2F5597'
  }));
  
  let content: string[] = [];
  
  // Classroom management notes
  if (lesson.classroom_management_notes && lesson.classroom_management_notes.length > 0) {
    content.push('Notes de Gestion de Classe:');
    lesson.classroom_management_notes.forEach((note, index) => {
      const sanitized = sanitizeTextForPowerPoint(note);
      if (sanitized) {
        content.push(`${index + 1}. ${sanitized}`);
      }
    });
    content.push('');
  }
  
  // Offline variant
  if (lesson.offline_variant && lesson.offline_variant.directions && lesson.offline_variant.directions.length > 0) {
    content.push('Variante Hors Ligne:');
    lesson.offline_variant.directions.forEach((direction, index) => {
      const sanitized = sanitizeTextForPowerPoint(direction);
      if (sanitized) {
        content.push(`${index + 1}. ${sanitized}`);
      }
    });
    content.push('');
  }
  
  // Home connection
  if (lesson.home_connection && lesson.home_connection.family_note) {
    content.push('Connexion avec la Maison:');
    const sanitized = sanitizeTextForPowerPoint(lesson.home_connection.family_note);
    if (sanitized) {
      content.push(sanitized);
    }
  }
  
  if (content.length > 0) {
    const contentText = sanitizeTextForPowerPoint(content.join('\n'));
    if (contentText && contentText.length > 0) {
      slide.addText(contentText, createSafeTextOptions({
        x: 0.8,
        y: 1.5,
        w: 8.4,
        h: 5,
        fontSize: 12,
        color: '333333'
      }));
    }
  }
}
